// CSS-string-escape a value so it can be dropped into an @page margin box.
function cssString(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/**
 * A small-caps running header (top-left of every page except the cover),
 * carrying a fixed label. Chrome can't do `string-set`, so we inject the
 * literal text into an @page margin box. The `:first` clear is injected right
 * after the header rule (and so cascades last) because Chrome won't let a
 * `@page :first` in a separate stylesheet override a plain `@page` margin box —
 * the cover would otherwise show the running header.
 */
export function runningHeaderStyle(label: string) {
  const css = `@page { @top-left {
       content: "${cssString(label)}";
       font-family: et-book, Georgia, serif;
       font-variant: small-caps;
       letter-spacing: 0.04em;
       font-size: 9pt;
       color: #000;
     } }
     @page :first { @top-left { content: ""; } @bottom-center { content: ""; } }`;
  return <style dangerouslySetInnerHTML={{ __html: css }} />;
}

/**
 * The bound-book page faces (?book=1): alternating sidenote gutters in a
 * SINGLE render, printable straight from CMD+P.
 *
 * @page :left/:right can't restyle content, so instead the content box spans
 * a sidenote gutter on BOTH sides of the text column (--gutter-pull each,
 * print.css "Bound-book layout" — every note renders twice, one copy floated
 * into each gutter), and the page face decides which gutter is on the paper:
 * the spine-side margin is NEGATIVE by exactly one gutter, hanging that
 * side's copies off the paper edge, where they're cropped.
 *
 * Geometry (letter, 215.9mm): binding 14 + text 119.9 + sep 16 + notes 61 +
 * outer 5 = 215.9. Content box = 77 + 119.9 + 77 = 273.9mm on BOTH faces
 * (identical width → one pagination), so margin(spine side) = 14 − 77 =
 * −63mm. The 16mm sep keeps the hidden gutter's notes 2mm clear of the
 * paper under the 14mm binding margin. Must stay in sync with the
 * .print-book tokens in print.css.
 *
 * The running header and page number move to margin boxes at the OUTER
 * corners: the plain @top-left/@bottom-center boxes key off the content box,
 * which now hangs off-paper on one side (top-left is literally off the sheet
 * on recto; bottom-center sits ~30mm off paper-center). Injected after
 * runningHeaderStyle so these margin boxes win on stylesheet order; the
 * cover's :first (and the named cover-verso/back-cover pages) clear the
 * corner boxes again below/in their own styles.
 */
export function bookPageStyle(label: string) {
  const boxFont = `font-family: et-book, Georgia, serif;
       font-size: 9pt;
       color: #000;`;
  const headFont = `${boxFont}
       font-variant: small-caps;
       letter-spacing: 0.04em;`;
  const css = `@page :right {
       margin-left: -63mm; margin-right: 5mm;
       @top-left { content: ""; }
       @bottom-center { content: ""; }
       @top-right { content: "${cssString(label)}"; ${headFont} }
       @bottom-right { content: counter(page); ${boxFont} }
     }
     @page :left {
       margin-left: 5mm; margin-right: -63mm;
       @top-left { content: "${cssString(label)}"; ${headFont} }
       @bottom-center { content: ""; }
       @bottom-left { content: counter(page); ${boxFont} }
     }
     @page :first {
       /* Re-assert the cover's margins (print.css @page :first): Chrome
        * resolves conflicting @page declarations by stylesheet order, not
        * page-selector specificity, so without this the :right rule above
        * would hang the cover's left edge off the paper. */
       margin: 12mm;
       @top-right { content: ""; }
       @bottom-left { content: ""; }
       @bottom-right { content: ""; }
     }`;
  return <style dangerouslySetInnerHTML={{ __html: css }} />;
}
